import { useEffect, useRef, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Mark, PlayIcon, PauseIcon, useRadio } from "../components/radio-shell";
import { entriesForDay } from "../lib/radio-schedule";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// ── SWU radio pages. Layout/header/footer/player live in RadioShell; each page
// renders its own content and pulls shared state via useRadio(). ──

function tierPrice(p: string): ReactNode {
  const m = p.match(/^(\S+)(\s+.*)?$/);
  if (!m) return p;
  return (
    <>
      {m[1]}
      {m[2] ? <span>{m[2]}</span> : null}
    </>
  );
}

// ── HOME: hero + on-air console (waveform) + highlights + events ──
export function RadioHomePage() {
  const { content, playing, toggle, listeners, now, liveTrack } = useRadio();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const playingRef = useRef(playing);
  useEffect(() => {
    playingRef.current = playing;
  }, [playing]);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const BARS = 44;
    const level = new Array(BARS).fill(0.1);
    let raf = 0;
    function resize() {
      if (!cv || !ctx) return;
      const r = cv.getBoundingClientRect();
      const d = window.devicePixelRatio || 1;
      cv.width = r.width * d;
      cv.height = r.height * d;
      ctx.setTransform(d, 0, 0, d, 0, 0);
    }
    resize();
    window.addEventListener("resize", resize);
    function draw() {
      if (!cv || !ctx) return;
      const w = cv.clientWidth;
      const h = cv.clientHeight;
      const mid = h / 2;
      ctx.clearRect(0, 0, w, h);
      const gap = 3;
      const bw = (w - (BARS - 1) * gap) / BARS;
      // Canvas can be 0-width (hidden on mobile, or pre-layout on first mount) →
      // bw goes negative → arcTo throws IndexSizeError. Skip until it has size.
      if (w <= 0 || bw <= 0) {
        if (!reduce) raf = requestAnimationFrame(draw);
        return;
      }
      const on = playingRef.current;
      for (let i = 0; i < BARS; i++) {
        const t = on
          ? 0.12 +
            (0.5 * Math.pow(1 - i / BARS, 1.2) + 0.35) *
              Math.abs(Math.sin(Date.now() / 230 + i * 0.55)) *
              (0.55 + Math.random() * 0.45)
          : 0.08 + 0.025 * Math.abs(Math.sin(Date.now() / 850 + i));
        level[i] += (t - level[i]) * 0.26;
        const bh = Math.max(2, level[i] * (h - 4));
        const x = i * (bw + gap);
        const y = mid - bh / 2;
        const g = ctx.createLinearGradient(0, y, 0, y + bh);
        if (on) {
          g.addColorStop(0, "#9a93ff");
          g.addColorStop(1, "#6c63e8");
        } else {
          g.addColorStop(0, "#2b2952");
          g.addColorStop(1, "#201f38");
        }
        ctx.fillStyle = g;
        const r = Math.max(0, Math.min(bw / 2, 1.6));
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.arcTo(x + bw, y, x + bw, y + bh, r);
        ctx.arcTo(x + bw, y + bh, x, y + bh, r);
        ctx.arcTo(x, y + bh, x, y, r);
        ctx.arcTo(x, y, x + bw, y, r);
        ctx.fill();
      }
      if (!reduce) raf = requestAnimationFrame(draw);
    }
    draw();
    return () => {
      window.removeEventListener("resize", resize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <>
      <div className="wrap hero" id="top">
        <Mark className="mark" />
        <h1>
          {content.wordmark}
          <span className="fm">{content.suffix ?? ""}</span>
        </h1>
        {content.tagline ? <p className="tag">{content.tagline}</p> : null}

        <div className="np">
          <div className="id">
            <div className="art">
              <Mark />
            </div>
            <div className="meta">
              <span className="st">
                <span className="live-dot breathe" />
                {now.current ? "On air" : "Off air"}
              </span>
              <div className="nm">{now.current?.show ?? content.live?.name ?? "Off air"}</div>
              <div className="su">
                {[now.current?.host ?? content.live?.with, now.current ? `since ${now.current.start}` : content.live?.since]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
              {liveTrack ? <div className="trk">♪ {liveTrack.text}</div> : null}
            </div>
          </div>
          <canvas className="wave" ref={canvasRef} aria-hidden="true" />
          <span className="lc">
            <b>{listeners.toLocaleString()}</b> live
          </span>
          <button className="listen" onClick={toggle}>
            {playing ? <PauseIcon /> : <PlayIcon />}
            <span>{playing ? "Stop" : "Listen"}</span>
          </button>
        </div>
      </div>

      <section className="blk wrap">
        <div className="sh">
          <div>
            <span className="lbl">This week</span>
            <h2>Highlights</h2>
          </div>
          <Link className="more" to="/shows">All shows →</Link>
        </div>
        <div className="rail">
          {content.highlights.map((s, i) => (
            <div className="card" key={i} onClick={() => !playing && toggle()}>
              <div className="media">
                <span className="tone" />
                {s.image ? <img src={s.image} alt="" /> : <Mark className="wmk" />}
                <span className="play">
                  <PlayIcon />
                </span>
              </div>
              <div className="cap">
                <div className="n">{s.name}</div>
                {s.with ? <div className="w">{s.with}</div> : null}
                {s.when ? <div className="dt">{s.when}</div> : null}
                {s.genres && s.genres.length ? (
                  <div className="g">
                    {s.genres.map((g, k) => (
                      <span key={k}>{g}</span>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="blk wrap">
        <div className="sh">
          <div>
            <span className="lbl">Off the airwaves</span>
            <h2>Events</h2>
          </div>
        </div>
        <div className="evs">
          {content.events.map((e, i) => (
            <a className="ev" key={i} href={e.href ?? "#"}>
              <div className="media">
                <span className="tone" />
                <Mark className="wmk" />
                <span className="date">{e.date}</span>
                <span className="nm">{e.name}</span>
              </div>
              <div className="er">
                <span className="ln">{e.lineup}</span>
                <span className="go">Tickets →</span>
              </div>
            </a>
          ))}
        </div>
      </section>
    </>
  );
}

// ── SCHEDULE: today's timetable, live/next derived from the clock ──
export function RadioSchedulePage() {
  const { content, now } = useRadio();
  const rows = entriesForDay(content.schedule, now.today);
  return (
    <section className="blk wrap">
      <div className="sh">
        <div>
          <span className="lbl">Today · {DAY_NAMES[now.today]}</span>
          <h2>Schedule</h2>
        </div>
      </div>
      <div className="log">
        {rows.map((e, i) => {
          const isLive = e === now.current;
          const isNext = e === now.next;
          return (
            <div className={isLive ? "slot live" : "slot"} key={i}>
              <span className="t">{e.start}</span>
              <span className="info">
                <div className="n">
                  {e.show}
                  {e.host ? `, ${e.host}` : ""}
                </div>
                {e.genres && e.genres.length ? <div className="g">{e.genres.join(" · ")}</div> : null}
              </span>
              <span className="st">
                {isLive ? (
                  <>
                    <span className="live-dot breathe" />
                    Live
                  </>
                ) : isNext ? (
                  "Up next"
                ) : (
                  ""
                )}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ── SHOWS: residents + on-demand archive ──
export function RadioShowsPage() {
  const { content, playing, toggle } = useRadio();
  return (
    <>
      <section className="blk wrap">
        <div className="sh">
          <div>
            <span className="lbl">The crew</span>
            <h2>Residents</h2>
          </div>
        </div>
        <div className="grid">
          {content.residents.map((r, i) => (
            <div className="res" key={i}>
              <div className="media">
                <span className="tone" />
                {r.image ? <img src={r.image} alt="" /> : <span className="ini">{r.initials}</span>}
              </div>
              <div className="rn">{r.name}</div>
              {r.genres ? <div className="rg">{r.genres}</div> : null}
            </div>
          ))}
        </div>
      </section>

      <section className="blk wrap">
        <div className="sh">
          <div>
            <span className="lbl">On demand</span>
            <h2>Archive</h2>
          </div>
        </div>
        <div className="log">
          {content.archive.map((a, i) => (
            <div className="slot" key={i} onClick={() => !playing && toggle()} style={{ cursor: "pointer" }}>
              <span className="t" style={{ color: "var(--peri-lt)" }}>
                <PlayIcon />
              </span>
              <span className="info">
                <div className="n">{a.name}</div>
                {a.genres ? <div className="g">{a.genres.join(" · ")}</div> : null}
              </span>
              <span className="st">
                {a.when}
                {a.duration ? ` · ${a.duration}` : ""}
              </span>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

// ── SHOP ──
export function RadioShopPage() {
  const { content } = useRadio();
  return (
    <section className="blk wrap">
      <div className="sh">
        <div>
          <span className="lbl">Merch</span>
          <h2>Shop</h2>
        </div>
      </div>
      <div className="shop">
        {content.shop.map((p, i) => (
          <a className="prod" key={i} href={p.href ?? "#"}>
            <div className="media">
              <span className="tone" />
              <Mark />
            </div>
            <div className="pn">{p.name}</div>
            <div className="pp">{p.price}</div>
          </a>
        ))}
      </div>
    </section>
  );
}

// ── SUPPORT ──
export function RadioSupportPage() {
  const { content } = useRadio();
  return (
    <section className="blk wrap">
      <div className="sh">
        <div>
          <span className="lbl">Keep us on air</span>
          <h2>Support</h2>
        </div>
      </div>
      <div className="tiers">
        {content.support.map((t, i) => (
          <div className={t.featured ? "tier on" : "tier"} key={i}>
            <div className="tn">{t.name}</div>
            <div className="pr">{tierPrice(t.price)}</div>
            <ul>
              {t.features.map((f, j) => (
                <li key={j}>{f}</li>
              ))}
            </ul>
            <span className="join">Join {t.name.toLowerCase()}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
